import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { CardSkeleton } from '../../Components/common/Skeleton';
import { ClipboardCheck, MapPin, Award, User, CreditCard } from 'lucide-react';
import { getAvatarUrl } from '../../utils/avatar';
import MarketingAgentEditModal from '../../Components/marketingAgents/MarketingAgentEditModal';


const MarketingAgentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalTab, setEditModalTab] = useState('profile');
  const [verificationStatus, setVerificationStatus] = useState('verified');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'customer_care' && user.role !== 'cc_head') {
      toast.error('Access Denied: Admin or CC/CC Head role required');
      navigate('/dashboard');
      return;
    }
    fetchAgent();
  }, [id, user]);

  const fetchAgent = async () => {
    try {
      const response = await api.get(`/marketing-agents/${id}`);
      setAgent(response.data.data);
    } catch (error) {
      toast.error('Failed to load Lead Partner');
      navigate('/marketing-agents');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setUpdating(true);
    try {
      await api.put(`/marketing-agents/${id}/verify`, {
        status: verificationStatus,
        rejectionReason: verificationStatus === 'rejected' ? rejectionReason : undefined
      });
      toast.success(`Lead Partner ${verificationStatus} successfully!`);
      setShowVerifyModal(false);
      setVerificationStatus('verified');
      setRejectionReason('');
      fetchAgent();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify Lead Partner');
    } finally {
      setUpdating(false);
    }
  };

  const getVerificationColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-slate-200 animate-pulse rounded-lg"></div>
            <div className="h-8 w-48 bg-slate-200 animate-pulse rounded-lg"></div>
          </div>
          <div className="h-10 w-32 bg-slate-100 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-6">
              <div className="h-6 w-1/3 bg-slate-100 rounded animate-pulse"></div>
              <div className="grid grid-cols-2 gap-8">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="space-y-2">
                    <div className="h-3 w-20 bg-slate-50 rounded animate-pulse"></div>
                    <div className="h-5 w-full bg-slate-100 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
            <CardSkeleton />
          </div>
          <div className="space-y-6">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Lead Partner not found</p>
        <Link to="/marketing-agents" className="text-primary-600 hover:text-primary-700">
          Back to Lead Partners
        </Link>
      </div>
    );
  }

  // Check if onboardingData contains banking info
  const onboarding = agent.user?.onboardingData || {};
  const banking = onboarding.bankDetails || onboarding.banking || {};
  const upiId = banking.upiId || onboarding.upiId || 'N/A';
  const allDocuments = [
    ...(agent.documents || []),
    ...(agent.user?.onboardingDocuments || [])
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg font-bold"
          >
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Lead Partner Details</h1>
        </div>
        <div className="flex space-x-2">
          {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') &&
            agent.verificationStatus === 'pending' && (
              <button
                onClick={() => setShowVerifyModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-bold"
              >
                Verify Agent
              </button>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Marketing Profile</h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('profile');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Profile
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Operating Region</p>
                <p className="font-medium">{agent.region || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Identification Number</p>
                <p className="font-medium">{agent.identificationNumber || onboarding.panNumber || onboarding.aadhaarNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Leads Count</p>
                <p className="font-medium">{agent.activeLeads || 0}</p>
              </div>

              {onboarding.panNumber && (
                <div>
                  <p className="text-sm text-gray-600">PAN Number</p>
                  <p className="font-medium">{onboarding.panNumber}</p>
                </div>
              )}
              {onboarding.aadhaarNumber && (
                <div>
                  <p className="text-sm text-gray-600">Aadhaar Number</p>
                  <p className="font-medium">{onboarding.aadhaarNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Banking / Financial Information */}
          <div className="bg-white rounded-lg shadow p-6 border-t-4 border-green-500">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Payout & Banking Details</h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('financial');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Banking
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Account Name</p>
                <p className="font-bold">{banking.accountName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Bank Name</p>
                <p className="font-bold">{banking.bankName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Account Number</p>
                <p className="font-bold">{banking.accountNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">IFSC Code</p>
                <p className="font-bold">{banking.ifscCode || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">UPI ID</p>
                <p className="font-bold text-blue-600">{upiId}</p>
              </div>
            </div>
          </div>

          {/* Uploaded Documents */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Verification Documents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {allDocuments && allDocuments.length > 0 ? (
                allDocuments.map((doc, idx) => {
                  const isImage = doc.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i);
                  const fullUrl = getAvatarUrl(doc.url);
                  return (
                    <div key={idx} className="flex flex-col border border-gray-100 rounded-2xl overflow-hidden bg-gray-50/30 group">
                      {isImage ? (
                        <div className="h-32 bg-gray-100 relative overflow-hidden">
                          <img
                            src={fullUrl}
                            alt={doc.name || doc.type}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors"></div>
                        </div>
                      ) : (
                        <div className="h-32 bg-blue-50 flex items-center justify-center text-blue-300">
                          <ClipboardCheck size={48} />
                        </div>
                      )}

                      <div className="p-4">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{doc.type?.replace(/_/g, ' ') || doc.name}</p>
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-bold text-[#143E78] hover:text-blue-600 flex items-center gap-2"
                        >
                          View Document ↗
                        </a>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-10 text-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-100">
                  <p className="text-sm text-gray-400 font-bold uppercase tracking-widest">No documents uploaded by agent</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('personal');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Personal
                </button>
              )}
            </div>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-50 overflow-hidden border-2 border-blue-100 shrink-0 flex items-center justify-center">
                {(() => {
                  const avatarUrl = agent.user?.avatar || agent.documents?.find(d => d.type === 'PHOTO')?.url;
                  const initials = (
                    <div className="w-full h-full flex items-center justify-center bg-blue-50 text-blue-500 font-black text-xl">
                      {agent.user?.firstName?.[0] || ''}{agent.user?.lastName?.[0] || ''}
                    </div>
                  );
                  if (avatarUrl) {
                    return (
                      <div className="relative w-full h-full">
                        <img
                          src={getAvatarUrl(avatarUrl)}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.parentNode.querySelector('.avatar-fallback');
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="avatar-fallback absolute inset-0 hidden items-center justify-center bg-blue-50 text-blue-500 font-black text-xl">
                          {agent.user?.firstName?.[0] || ''}{agent.user?.lastName?.[0] || ''}
                        </div>
                      </div>
                    );
                  }
                  return initials;
                })()}
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black font-semibold">Lead Partner</p>
                <p className="font-bold text-lg text-slate-800 leading-tight">
                  {agent.user?.title ? `${agent.user.title} ` : ''}{agent.user?.firstName} {agent.user?.lastName}
                </p>
              </div>
            </div>
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Email</p>
                <p className="font-medium text-slate-700">{agent.user?.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Phone</p>
                <p className="font-medium text-slate-700">{agent.user?.phone}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Alternative Phone</p>
                <p className="font-medium text-slate-700">{agent.user?.alternativePhone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Gender</p>
                <p className="font-bold text-slate-700">{agent.user?.gender || onboarding.gender || 'Not Specified'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Date of Birth</p>
                <p className="font-bold text-slate-700">
                  {agent.user?.dob 
                    ? new Date(agent.user.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) 
                    : (onboarding.dob 
                        ? new Date(onboarding.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) 
                        : 'Not Specified')}
                </p>
              </div>
            </div>
          </div>

          {/* Geographic Profile */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-500" />
                Geographic Profile
              </h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('address');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Address
                </button>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black">Street Address</p>
                <p className="font-medium text-slate-700">{agent.user?.address?.street || onboarding.currentAddress || 'N/A'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">City</p>
                  <p className="font-medium text-slate-700">{agent.user?.address?.city || onboarding.city || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">State</p>
                  <p className="font-medium text-slate-700">{agent.user?.address?.state || onboarding.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Pincode</p>
                  <p className="font-medium text-slate-700">{agent.user?.address?.pincode || onboarding.pincode || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-black">Country</p>
                  <p className="font-medium text-slate-700">{agent.user?.address?.country || 'India'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Log info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h2>
            <span className={`px-3 py-1 text-sm rounded-full ${getVerificationColor(agent.verificationStatus)}`}>
              {agent.verificationStatus}
            </span>
            {agent.verifiedBy && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">CC (Verified By)</p>
                <p className="font-medium text-indigo-600">{agent.verifiedBy.firstName} {agent.verifiedBy.lastName}</p>
              </div>
            )}
            {agent.user?.invitedBy && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">CC (Invited By)</p>
                <p className="font-medium text-slate-600">{agent.user.invitedBy.firstName} {agent.user.invitedBy.lastName}</p>
              </div>
            )}
            {agent.rejectionReason && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">Rejection Reason</p>
                <p className="font-medium text-red-600">{agent.rejectionReason}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Verify Lead Partner</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={verificationStatus}
                  onChange={(e) => setVerificationStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              {verificationStatus === 'rejected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowVerifyModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVerify}
                disabled={updating}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {updating ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <MarketingAgentEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        agent={agent}
        onSuccess={fetchAgent}
        defaultTab={editModalTab}
      />
    </div>
  );
};

export default MarketingAgentDetail;
